import { Button, Checkbox, Input, RadioGroup, type RadioGroupRenderItem } from "@synthesis/shared-ui";
import type { ReactNode } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { applicantDataSchema, usePageStore, type ApplicantDataFormValues } from "../../create-application-page.lib";

type ApplicantBooleanField =
  | "actualAddressMatchesLegal"
  | "postalAddressMatchesRegistration"
  | "useSeparateContractEmail"
  | "paymentByAnotherOrganization"
  | "payerActualAddressMatchesLegal"
  | "payerPostalAddressMatchesRegistration"
  | "payerUseSeparateContractEmail";

type ApplicantStringArrayField =
  | "phones"
  | "emails"
  | "contractEmails"
  | "payerPhones"
  | "payerEmails"
  | "payerContractEmails"
  | "payerPaymentContactPhones"
  | "payerPaymentContactEmails";

type ApplicantRadioOption = {
  value: string;
  label: string;
};

type ApplicantRadioGroupProps = {
  name: "applicantType" | "signerType" | "payerApplicantType";
  options: ApplicantRadioOption[];
  horizontal?: boolean;
  onChange?: (value: string) => void;
};

export const ApplicantRadioGroup = ({ name, options, horizontal = false, onChange }: ApplicantRadioGroupProps) => {
  const methods = useFormContext<ApplicantDataFormValues>();
  const pageStore = usePageStore();

  const items: RadioGroupRenderItem[] = options.map((option) => ({
    id: `${name}-${option.value}`,
    value: option.value,
    label: option.label,
  }));

  const handleChange = (value: string) => {
    methods.clearErrors();
    const nextValues = { ...methods.getValues(), [name]: value };
    pageStore?.setApplicantStepValid(applicantDataSchema.safeParse(nextValues).success);
    onChange?.(value);
  };

  return <RadioGroup name={name} renderRadioGroupElements={items} horizontal={horizontal} onClick={handleChange} />;
};

type ApplicantCheckboxProps = {
  name: ApplicantBooleanField;
  label: string;
  onCheckedChange?: (checked: boolean) => void;
};

export const ApplicantCheckbox = ({ name, label, onCheckedChange }: ApplicantCheckboxProps) => {
  const methods = useFormContext<ApplicantDataFormValues>();
  const checked = useWatch({ name });

  const handleChange = (value: boolean | "indeterminate" | undefined) => {
    const nextValue = value === true;
    methods.setValue(name, nextValue, { shouldValidate: true });
    onCheckedChange?.(nextValue);
  };

  return <Checkbox checked={checked} label={label} onChange={handleChange} />;
};

type ApplicantStringListProps = {
  name: ApplicantStringArrayField;
  placeholder: string;
  addLabel: string;
  removeLabel: string;
  footerAction?: ReactNode;
};

export const ApplicantStringList = ({ name, placeholder, addLabel, removeLabel, footerAction }: ApplicantStringListProps) => {
  const methods = useFormContext<ApplicantDataFormValues>();
  const values = useWatch<ApplicantDataFormValues, ApplicantStringArrayField>({ name });

  const appendValue = () => {
    methods.setValue(name, [...values, ""], { shouldValidate: true });
  };

  const removeValue = (index: number) => {
    methods.setValue(
      name,
      values.filter((_, valueIndex) => valueIndex !== index),
      { shouldValidate: true },
    );
  };

  return (
    <div className="flex flex-col gap-2">
      {values.map((_, index) => (
        <div key={`${name}-${index}`} className="flex flex-col gap-2">
          <Input name={`${name}.${index}`} placeholder={placeholder} />
          {values.length > 1 && (
            <Button type="button" theme="secondary" size="sm" className="w-fit" onClick={() => removeValue(index)}>
              {removeLabel}
            </Button>
          )}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-4">
        <Button type="button" theme="secondary" size="sm" className="w-fit" onClick={appendValue}>
          {addLabel}
        </Button>
        {footerAction}
      </div>
    </div>
  );
};
